import { Badge, Button, Card, Select, Toggle, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ConnectorResponse, ConnectorToolResponse, ConnectorToolUpdate } from "@/api";
import {
  errorMessage,
  getConnectorDetailQueryKey,
  listConnectorToolsOptions,
  listConnectorToolsQueryKey,
  refreshConnectorToolsMutation,
  updateConnectorToolMutation,
} from "@/api";
import { Text } from "@/components/Text";
import { formatRelativeTime } from "@/i18n/format";

const CAPABILITIES = ["context", "action", "artifact"] as const;
type Capability = (typeof CAPABILITIES)[number];

function ToolRow({
  tool,
  canEdit,
  onChange,
}: Readonly<{ tool: ConnectorToolResponse; canEdit: boolean; onChange: (body: ConnectorToolUpdate) => void }>) {
  const { t } = useTranslation("connectors");
  const capability = tool.capability as Capability;
  const sources: Record<string, string> = { owner: t("tools.byOwner"), server_hint: t("tools.fromServer") };
  const source = sources[tool.classifiedBy] ?? t("tools.default");
  return (
    <li className="flex flex-col gap-2 py-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-6">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2">
            <Text as="span" variant="body-sm" className="[font:var(--type-code)]">
              {tool.name}
            </Text>
            {!canEdit && <Badge size="compact">{t(`tools.capabilities.${capability}`)}</Badge>}
            {!canEdit && tool.escalation === "required" && (
              <Badge size="compact" variant="warning">
                {t("tools.asksFirst")}
              </Badge>
            )}
          </span>
          {tool.description && (
            <Text variant="caption" tone="tertiary" className="line-clamp-2">
              {tool.description}
            </Text>
          )}
          <Text variant="caption" tone="tertiary">
            {source}
          </Text>
        </div>
        {canEdit && (
          <div className="flex shrink-0 flex-wrap items-center gap-4">
            <div className="w-40">
              <Select
                aria-label={t("tools.capability")}
                value={capability}
                options={CAPABILITIES.map((value) => ({ value, label: t(`tools.capabilities.${value}`) }))}
                onChange={(value) => onChange({ capability: value as Capability })}
              />
            </div>
            <Toggle
              label={t("tools.asksFirst")}
              checked={tool.escalation === "required"}
              onChange={(on) => onChange({ escalation: on ? "required" : "none" })}
            />
            <Toggle label={t("tools.on")} checked={tool.isEnabled} onChange={(on) => onChange({ isEnabled: on })} />
            {tool.classifiedBy === "owner" && (
              <Button variant="ghost" size="sm" onClick={() => onChange({ reset: true })}>
                {t("tools.reset")}
              </Button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/** What the assistant can do with this connector; editors refresh and reclassify. */
export function ConnectorToolsTab({ connector }: Readonly<{ connector: ConnectorResponse }>) {
  const { t } = useTranslation("connectors");
  const toast = useToast();
  const queryClient = useQueryClient();
  const path = { connector_uuid: connector.uuid };
  const tools = useQuery({ ...listConnectorToolsOptions({ path }), select: (r) => r.data });
  const refresh = useMutation(refreshConnectorToolsMutation());
  const update = useMutation(updateConnectorToolMutation());
  const canEdit = connector.currentUserRole === "admin" || connector.currentUserRole === "editor";
  const invalidate = () =>
    Promise.all(
      [listConnectorToolsQueryKey({ path }), getConnectorDetailQueryKey({ path })].map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    );

  const check = () =>
    refresh.mutate(
      { path },
      {
        onSuccess: async () => {
          await invalidate();
          toast.success(t("tools.refreshed"));
        },
        onError: async (error) => {
          await invalidate();
          toast.error(t("tools.refreshFailed", { error: errorMessage(error, "") }));
        },
      },
    );

  const change = (tool: ConnectorToolResponse, body: ConnectorToolUpdate) =>
    update.mutate(
      { path: { ...path, tool_uuid: tool.uuid }, body },
      {
        onSuccess: async () => {
          await invalidate();
          toast.success(t("tools.saved", { name: tool.name }));
        },
        onError: (error) => toast.error(errorMessage(error, t("tools.saveFailed", { name: tool.name }))),
      },
    );

  const rows = tools.data ?? [];
  return (
    <Card>
      <section aria-label={t("tools.title")} className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="flex flex-col gap-1">
            <Text variant="body">{t("tools.title")}</Text>
            <Text variant="body-sm" tone="secondary">
              {t("tools.body")}
            </Text>
            <Text variant="caption" tone="tertiary">
              {connector.lastRefreshedAt
                ? t("tools.lastChecked", { when: formatRelativeTime(connector.lastRefreshedAt) })
                : t("tools.never")}
            </Text>
          </div>
          {canEdit && (
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<RefreshCw size={14} />}
              loading={refresh.isPending}
              onClick={check}
            >
              {t("tools.refresh")}
            </Button>
          )}
        </div>
        {tools.isSuccess && rows.length === 0 && (
          <Text variant="body-sm" tone="tertiary">
            {t("tools.empty")}
          </Text>
        )}
        {rows.length > 0 && (
          <ul aria-label={t("tools.title")} className="flex flex-col divide-y divide-border-subtle">
            {rows.map((tool) => (
              <ToolRow key={tool.uuid} tool={tool} canEdit={canEdit} onChange={(body) => change(tool, body)} />
            ))}
          </ul>
        )}
      </section>
    </Card>
  );
}
